<template>
  <FullScreenToggler class="width-all">
    <p v-if="compiledFileItem.error">{{compiledFileItem.error}}</p>
    <p v-else-if="playbackError" class="playback-error">{{playbackError}}</p>
    <div 
      v-else 
      :id="videoId" 
      class="videw-container width-all content" 
      ref="elContainer"
      @contextmenu.prevent
      @dragstart.prevent
      @selectstart.prevent
    >
      <video 
        class=" width-all content" 
        ref="elVideo" 
        controls
        @contextmenu.prevent
        @dragstart.prevent
        controlsList="nodownload nofullscreen"
      ></video>
    </div>
  </FullScreenToggler>
</template>

<script>
import Hls from 'hls.js';
import { elementService } from '../../common/services/element.service';
import { fixFileSrcToThumbnail } from '../../common/services/file.service';
import { Utils } from '../../common/services/util.service';
import FullScreenToggler from '../../common/cmps/FullScreenToggler.vue';
import config from '@/config';

const mediaSessionService = window.mediaSessionModule;
const {VideoTagWatermarkService} = window.VideoTagWatermarkModule;

export default {
  name: 'VideoTag',
  components: { FullScreenToggler },
  props: {
    src: String,
    format: String,
    fileItem: Object,
    compiledFileItem: Object,
    fileId: String,
    organizationId: String,
    releaseId: String,
    // type: String
  },
  data() {
    return {
      videoId: Utils.getRandomId(''),
      // watermarkInterval: null,
      SessionService: null,
      WatermarkService: null,
      keyboardHandler: null,
      playbackError: null,
      nativeWatermarkTrackEl: null,
      nativeWatermarkBlobUrl: null,
      nativeWatermarkRefreshInterval: null,
      nativeWatermarkMode: 'none', // none | fullscreen | pip
      nativeEventsCleanup: null,
    }
  },
  watch: {
    src() {
      this.destroy();
      this.init();
    },
    fileId() {
      this.destroy();
      this.init();
    },
    organizationId() {
      this.destroy();
      this.init();
    },
    releaseId() {
      this.destroy();
      this.init();
    }
  },
  mounted() {
    this.init();
    this.preventSaveActions();
  },
  destroyed() {
    this.destroy();
    this.removeSavePrevention();
  },
  computed: {
    organization() {
      return this.$store.getters['organization/selectedItem'];
    },
    loggedUser() {
      return this.$store.getters['auth/loggedUser'];
    },
    useWterMark() {
      return this.organization.useVideoWaterMark;
    },
    watermarkMsg() {
      // return `${this.organization.name} - ${this.loggedUser.firstName} ${this.loggedUser.lastName} | ${this.loggedUser.email} | ${this.loggedUser.mobile}`
      return `${this.loggedUser.firstName} ${this.loggedUser.lastName} | ${this.loggedUser.email} | ${this.loggedUser.mobile}`
    },
    logoUrl() {
      return fixFileSrcToThumbnail(this.organization.logo);
    },

    logSessions() {
      return this.organization.restrictVideos;
    }
  },
  methods: {
    async init() {
      const { elVideo } = this.$refs;
      if (!elVideo) return;

      this.initWatchSession();
      this.startDomWatermark();
      if (!this.isNativeSubtitleWatermarkSupported()) {
        this.unbindNativeWatermarkEvents();
        this.stopNativeWatermark();
      }

      // For encrypted / yes-org: when we have fileId + organizationId but no play URL, fetch play URL first.
      let effectiveSrc = this.src;
      if (this.fileId && this.organizationId && !this.getPlayTokenFromSrc(this.src)) {
        try {
          effectiveSrc = await this.fetchPlayUrl(this.organizationId, this.fileId, this.releaseId);
          if (!effectiveSrc) {
            this.playbackError = this.$t ? this.$t('releaseLocales.playbackError') || 'Could not load video' : 'Could not load video';
            return;
          }
        } catch (e) {
          this.playbackError = this.$t ? this.$t('releaseLocales.playbackError') || 'Could not load video' : 'Could not load video';
          return;
        }
      }

      if (!effectiveSrc) return;
      if (this.isNativeSubtitleWatermarkSupported()) {
        this.bindNativeWatermarkEvents();
      } else {
        this.unbindNativeWatermarkEvents();
        this.stopNativeWatermark();
      }

      // HLS: .m3u8 or KAN-style play URL (/file/play/TOKEN — no .m3u8 in path but backend serves m3u8)
      const isHls = (this.format === 'm3u8') ||
        effectiveSrc?.split('?')[0]?.endsWith('.m3u8') ||
        (effectiveSrc && /\/file\/play\//.test(effectiveSrc));
      if (!isHls) {
        elVideo.src = effectiveSrc;
        return;
      }

      // Play URL (KAN-style): require playback session and X-Playback-Session header on every request.
      const playToken = this.getPlayTokenFromSrc(effectiveSrc);
      if (playToken) {
        this.playbackError = null;
        const sessionId = await this.fetchPlaybackSession(playToken);
        if (!sessionId) {
          this.playbackError = this.$t ? this.$t('releaseLocales.playbackAlreadyInUse') || 'Playback already in use' : 'Playback already in use';
          return;
        }
        const hls = new Hls({
          maxBufferLength: 30,
          maxMaxBufferLength: 120,
          xhrSetup: (xhr) => {
            xhr.withCredentials = true;
            xhr.setRequestHeader('X-Playback-Session', sessionId);
          }
        });
        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            this.playbackError = (data.details || data.type) || 'Playback error';
          }
        });
        hls.loadSource(effectiveSrc);
        hls.attachMedia(elVideo);
        elVideo.controlsList = 'nodownload nofullscreen';
        this.hls = hls;
        return;
      }

      // HLS: never set video.src to a URL. HLS.js fetches via backend and uses MSE (Media Source
      // Extensions), so the video element gets a blob URL only — no media URL is exposed in the DOM.
      const hls = new Hls();
      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          this.playbackError = (data.details || data.type) || 'Playback error';
        }
      });
      hls.loadSource(effectiveSrc);
      hls.attachMedia(elVideo);
      elVideo.controlsList = "nodownload nofullscreen"
      this.hls = hls;
    },
    async fetchPlayUrl(organizationId, fileId, releaseId) {
      const base = config.baseApiUrl.replace(/\/$/, '');
      let url = `${base}/file/url/${encodeURIComponent(organizationId)}/${encodeURIComponent(fileId)}`;
      if (releaseId) url += `?releaseId=${encodeURIComponent(releaseId)}`;
      try {
        const res = await fetch(url, { method: 'GET', credentials: 'include' });
        if (!res.ok) return null;
        const data = await res.json();
        return data.url || null;
      } catch (e) {
        return null;
      }
    },
    getPlayTokenFromSrc(src) {
      if (!src || typeof src !== 'string') return null;
      const m = src.match(/\/file\/play\/([^/?]+)/);
      return m ? m[1] : null;
    },
    async fetchPlaybackSession(token) {
      const base = config.baseApiUrl.replace(/\/$/, '');
      const url = `${base}/file/play-session/${encodeURIComponent(token)}`;
      try {
        const res = await fetch(url, { method: 'GET', credentials: 'include' });
        if (res.status === 403) return null;
        if (!res.ok) return null;
        const data = await res.json();
        return data.sessionId || null;
      } catch (e) {
        return null;
      }
    },
    destroy() {
      this.unbindNativeWatermarkEvents();
      this.stopNativeWatermark();
      this.hls?.destroy();
      this.hls = null;
      this.SessionService?.destroy();
      this.stopDomWatermark();
      this.playbackError = null;
    },


    async initWatchSession() {
      if (!this.logSessions) return;
      
      this.SessionService = new mediaSessionService.MediaPlaySession(this.organization, this.loggedUser, this.fileItem, this.$refs.elVideo, config.baseApiUrl);
    },
    isNativeSubtitleWatermarkSupported() {
      if (typeof navigator === 'undefined') return false;
      const ua = navigator.userAgent || '';
      const isAndroid = /Android/i.test(ua);
      if (isAndroid) return false;
      const isIOS = /iPhone|iPad|iPod/i.test(ua);
      const isMac = /Macintosh|Mac OS X/i.test(ua);
      return isIOS || isMac;
    },

    startDomWatermark() {
      if (!this.useWterMark) return;
      if (!this.$refs.elVideo || !this.$refs.elContainer) return;
      if (this.WatermarkService) return;
      this.WatermarkService = new VideoTagWatermarkService(
        this.$refs.elVideo,
        this.$refs.elContainer,
        this.loggedUser.email || this.watermarkMsg.split('|').join('</p><p>')
      );
    },
    stopDomWatermark() {
      this.WatermarkService?.destroy();
      this.WatermarkService = null;
    },
    getNativeWatermarkMessage() {
      return String(this.loggedUser?.email || this.watermarkMsg || '').trim();
    },
    formatVttTime(seconds) {
      const s = Math.max(0, Number(seconds) || 0);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = Math.floor(s % 60);
      const ms = Math.floor((s - Math.floor(s)) * 1000);
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
    },
    buildNativeWatermarkVtt(message, { forPip = false } = {}) {
      const wmText = String(message || '').trim();
      if (!wmText) return 'WEBVTT\n\n';
      if (forPip) {
        return `WEBVTT

STYLE
::cue {
  color: rgba(255, 255, 255, 0.34);
  background-color: rgba(0, 0, 0, 0);
  text-shadow: none;
  font-size: 86%;
}

00:00:00.000 --> 23:59:59.000
${wmText}

`;
      }
      const cueCount = 900;
      const interval = 2.5;
      const duration = 2.5;
      const lines = [6, 14, 22, 30, 38, 46, 54, 62, 70, 78, 86];
      const positions = [4, 12, 20, 28, 36, 44, 52, 60, 68, 76, 84];
      let vtt = `WEBVTT

STYLE
::cue {
  color: rgba(255, 255, 255, 0.34);
  background-color: rgba(0, 0, 0, 0);
  text-shadow: none;
  font-size: 86%;
}

`;
      for (let i = 0; i < cueCount; i += 1) {
        const start = i * interval;
        const end = start + duration;
        const line = lines[Math.floor(Math.random() * lines.length)];
        const position = positions[Math.floor(Math.random() * positions.length)];
        const size = 16 + Math.floor(Math.random() * 8);
        vtt += `${this.formatVttTime(start)} --> ${this.formatVttTime(end)} align:start line:${line}% position:${position}% size:${size}%\n`;
        const marker = i % 2 === 0 ? '\u200B' : '\u200C';
        vtt += `${wmText}${marker}\n\n`;
      }
      return vtt;
    },
    applyNativeWatermarkTrackSource(message, mode = 'showing', { forPip = false } = {}) {
      const video = this.$refs.elVideo;
      if (!video) return null;
      const desiredKind = forPip ? 'captions' : 'subtitles';
      let trackEl = this.nativeWatermarkTrackEl;
      if (trackEl && trackEl.kind !== desiredKind) {
        try { if (video.contains(trackEl)) video.removeChild(trackEl); } catch (err) {}
        trackEl = null;
        this.nativeWatermarkTrackEl = null;
      }
      if (!trackEl) {
        trackEl = document.createElement('track');
        trackEl.kind = desiredKind;
        trackEl.label = 'Secure Watermark';
        trackEl.srclang = 'he';
        video.appendChild(trackEl);
        this.nativeWatermarkTrackEl = trackEl;
      }
      const vttText = this.buildNativeWatermarkVtt(message, { forPip });
      const blob = new Blob([vttText], { type: 'text/vtt' });
      const nextUrl = URL.createObjectURL(blob);
      const prevUrl = this.nativeWatermarkBlobUrl;
      this.nativeWatermarkBlobUrl = nextUrl;
      trackEl.src = nextUrl;

      const track = trackEl.track;
      if (track) {
        try { track.mode = mode; } catch (err) {}
      }
      try {
        for (let i = 0; i < video.textTracks.length; i += 1) {
          video.textTracks[i].mode = video.textTracks[i] === track ? mode : 'hidden';
        }
      } catch (err) {}

      if (prevUrl) {
        setTimeout(() => {
          try { URL.revokeObjectURL(prevUrl); } catch (err) {}
        }, 1000);
      }
      return track;
    },
    stopNativeWatermark() {
      if (this.nativeWatermarkRefreshInterval) {
        clearInterval(this.nativeWatermarkRefreshInterval);
        this.nativeWatermarkRefreshInterval = null;
      }
      if (this.nativeWatermarkBlobUrl) {
        try { URL.revokeObjectURL(this.nativeWatermarkBlobUrl); } catch (err) {}
        this.nativeWatermarkBlobUrl = null;
      }
      const video = this.$refs.elVideo;
      if (this.nativeWatermarkTrackEl) {
        try { this.nativeWatermarkTrackEl.track.mode = 'hidden'; } catch (err) {}
        try {
          if (video && video.contains(this.nativeWatermarkTrackEl)) {
            video.removeChild(this.nativeWatermarkTrackEl);
          }
        } catch (err) {}
      }
      this.nativeWatermarkTrackEl = null;
      this.nativeWatermarkMode = 'none';
    },
    startNativeWatermark({ forPip = false } = {}) {
      if (!this.isNativeSubtitleWatermarkSupported()) return;
      const message = this.getNativeWatermarkMessage();
      if (!message || !this.useWterMark) return;
      this.stopDomWatermark();
      this.stopNativeWatermark();
      const track = this.applyNativeWatermarkTrackSource(message, 'showing', { forPip });
      if (!track) return;
      this.nativeWatermarkMode = forPip ? 'pip' : 'fullscreen';
    },
    onNativePresentationMode(mode) {
      if (!this.isNativeSubtitleWatermarkSupported()) return;
      if (!this.useWterMark) return;
      if (mode === 'pip') {
        this.startNativeWatermark({ forPip: true });
        return;
      }
      if (mode === 'fullscreen') {
        this.startNativeWatermark();
        return;
      }
      // inline
      this.stopNativeWatermark();
      this.startDomWatermark();
    },
    bindNativeWatermarkEvents() {
      this.unbindNativeWatermarkEvents();
      if (!this.isNativeSubtitleWatermarkSupported()) return;
      const video = this.$refs.elVideo;
      if (!video || !this.useWterMark) return;

      const onFullscreenChange = () => {
        const fsEl = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
        this.onNativePresentationMode(fsEl ? 'fullscreen' : 'inline');
      };
      const onEnterPiP = () => this.onNativePresentationMode('pip');
      const onLeavePiP = () => {
        const fsEl = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
        this.onNativePresentationMode(fsEl ? 'fullscreen' : 'inline');
      };
      const onWebkitModeChanged = () => {
        const mode = video.webkitPresentationMode;
        if (mode === 'picture-in-picture') return this.onNativePresentationMode('pip');
        if (mode === 'fullscreen') return this.onNativePresentationMode('fullscreen');
        return this.onNativePresentationMode('inline');
      };

      document.addEventListener('fullscreenchange', onFullscreenChange);
      document.addEventListener('webkitfullscreenchange', onFullscreenChange);
      video.addEventListener('enterpictureinpicture', onEnterPiP);
      video.addEventListener('leavepictureinpicture', onLeavePiP);
      video.addEventListener('webkitpresentationmodechanged', onWebkitModeChanged);

      this.nativeEventsCleanup = () => {
        document.removeEventListener('fullscreenchange', onFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
        video.removeEventListener('enterpictureinpicture', onEnterPiP);
        video.removeEventListener('leavepictureinpicture', onLeavePiP);
        video.removeEventListener('webkitpresentationmodechanged', onWebkitModeChanged);
      };
    },
    unbindNativeWatermarkEvents() {
      if (this.nativeEventsCleanup) {
        this.nativeEventsCleanup();
        this.nativeEventsCleanup = null;
      }
    },
    
    preventSaveActions() {
      // Prevent keyboard shortcuts (Ctrl+S, Ctrl+Shift+S, etc.)
      this.keyboardHandler = (e) => {
        if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
          e.preventDefault();
          return false;
        }
        // Prevent F12 (Developer Tools) and other inspection shortcuts
        if (e.key === 'F12' || ((e.ctrlKey || e.metaKey) && (e.shiftKey && e.key === 'I'))) {
          e.preventDefault();
          return false;
        }
      };
      document.addEventListener('keydown', this.keyboardHandler);
      
      // Prevent text selection on video container
      const { elContainer, elVideo } = this.$refs;
      if (elContainer) {
        elContainer.style.userSelect = 'none';
        elContainer.style.webkitUserSelect = 'none';
      }
      if (elVideo) {
        elVideo.style.userSelect = 'none';
        elVideo.style.webkitUserSelect = 'none';
      }
    },
    
    removeSavePrevention() {
      if (this.keyboardHandler) {
        document.removeEventListener('keydown', this.keyboardHandler);
        this.keyboardHandler = null;
      }
    },
    
  },
}


</script>

<style lang="scss">
@import '@/assets/styles/global/index';
.videw-container {
  position: relative;
  overflow: hidden;
  video::-internal-media-controls-download-button {
    display: none !important;
  }
  video::-webkit-media-controls-enclosure {
    overflow: hidden !important;
  }


  video {
    height: auto;
    object-fit: contain;
    width: 100%;
    height: 100%;
  }
  video::-webkit-media-controls-fullscreen-button {
    display: none
  }
}
</style>